<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatCredits, formatUsdFromCents } from '../helpers/money';
import { formatBillingDateTime } from '../helpers/formatBillingTime';

const props = defineProps({
  credits: { type: Object, default: null },
  catalog: { type: Object, default: null },
  isPro: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['buy']);

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const packCount = ref(1);

const packSize = computed(
  () => Number(props.catalog?.credits_pack_size) || 500000
);
const packPrice = computed(
  () => Number(props.catalog?.credits_pack_price_cents) || 2000
);
const purchaseCap = computed(
  () => Number(props.catalog?.credits_purchase_cap) || 10000000
);
const maxPacks = computed(() =>
  Math.max(1, Math.floor(purchaseCap.value / packSize.value))
);

const estimatedCredits = computed(() => packCount.value * packSize.value);
const estimatedCents = computed(() => packCount.value * packPrice.value);

const cycleBucket = computed(() =>
  (props.credits?.buckets || []).find(b => b.bucket_type === 'plan_cycle')
);
const purchasedBuckets = computed(() =>
  (props.credits?.buckets || []).filter(b => b.bucket_type === 'purchased')
);

const onBuy = () => {
  emit('buy', {
    order_type: 'ai_credits',
    pack_count: packCount.value,
    amount_cents: estimatedCents.value,
    credits: estimatedCredits.value,
  });
};
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.CREDITS.TITLE')"
    :description="t('CUSTOM.BILLING.CREDITS.DESCRIPTION')"
  >
    <template #action>
      <RouterLink
        :to="accountScopedRoute('custom_ai_usage')"
        class="text-xs font-medium text-n-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded"
      >
        {{ t('CUSTOM.BILLING.CREDITS.VIEW_USAGE') }}
      </RouterLink>
    </template>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <div class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.CREDITS.AVAILABLE') }}
        </div>
        <div class="mt-1 text-lg font-semibold text-n-slate-12">
          {{ formatCredits(credits?.available_credits) }}
        </div>
      </div>
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <div class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.CREDITS.CYCLE') }}
        </div>
        <div class="mt-1 text-sm font-medium text-n-slate-12">
          <template v-if="cycleBucket">
            {{ formatCredits(cycleBucket.remaining) }}
            /
            {{ formatCredits(cycleBucket.allowance) }}
            <span class="block text-xs text-n-slate-11 mt-1">
              {{
                t('CUSTOM.BILLING.CREDITS.EXPIRES', {
                  when: formatBillingDateTime(cycleBucket.expires_at),
                })
              }}
            </span>
          </template>
          <template v-else>—</template>
        </div>
      </div>
    </div>

    <div v-if="purchasedBuckets.length" class="mb-4">
      <h3 class="text-xs font-medium text-n-slate-11 mb-2">
        {{ t('CUSTOM.BILLING.CREDITS.PURCHASED_PACKS') }}
      </h3>
      <ul class="m-0 list-none space-y-2">
        <li
          v-for="bucket in purchasedBuckets"
          :key="bucket.id"
          class="flex justify-between gap-3 rounded-lg border border-n-weak px-3 py-2 text-sm"
        >
          <span class="text-n-slate-12">
            {{ formatCredits(bucket.remaining) }}
            /
            {{ formatCredits(bucket.allowance) }}
          </span>
          <span class="text-n-slate-11 whitespace-nowrap">
            {{ formatBillingDateTime(bucket.expires_at) }}
          </span>
        </li>
      </ul>
    </div>
    <p v-else class="mb-4 text-sm text-n-slate-11">
      {{ t('CUSTOM.BILLING.CREDITS.NO_PURCHASED') }}
    </p>

    <div
      v-if="isPro"
      class="flex flex-wrap items-end gap-3 rounded-lg border border-n-weak p-3"
    >
      <div class="flex flex-col gap-1">
        <label class="text-xs text-n-slate-11" for="credits-pack-count">
          {{ t('CUSTOM.BILLING.CREDITS.PACKS') }}
        </label>
        <input
          id="credits-pack-count"
          v-model.number="packCount"
          type="number"
          min="1"
          :max="maxPacks"
          step="1"
          class="box-border h-9 w-24 px-2.5 bg-n-alpha-1 border border-n-weak rounded text-sm text-n-slate-12"
        />
      </div>
      <div class="text-sm text-n-slate-11 pb-2">
        {{ formatCredits(estimatedCredits) }}
        ·
        {{ formatUsdFromCents(estimatedCents) }}
      </div>
      <Button
        size="sm"
        color="blue"
        class="ms-auto"
        :label="t('CUSTOM.BILLING.CREDITS.BUY')"
        :is-loading="submitting"
        :disabled="submitting || packCount < 1"
        @click="onBuy"
      />
    </div>
    <p v-else class="mb-0 text-sm text-n-slate-11">
      {{ t('CUSTOM.BILLING.CREDITS.PRO_ONLY') }}
    </p>
  </BillingSectionCard>
</template>
